import {
  FocusEvent,
  useState,
  ChangeEvent,
  useEffect,
  KeyboardEvent,
  DetailedHTMLProps,
  InputHTMLAttributes,
  MouseEvent,
} from 'react';
import classNames from 'classnames';
import './InputPin.scss';

export type InputPinAttributes = DetailedHTMLProps<
  InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
>;

export interface InputPinProps extends Omit<Partial<InputPinAttributes>, 'size' | 'type'> {
  size?: 'xs' | 'small' | 'medium' | 'large';
  error?: boolean;
  type?: HTMLInputElement['type'];
  inputRef?: React.RefObject<HTMLInputElement>;
  autoFocus?: boolean;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  autoComplete?: HTMLInputElement['autocomplete'];
  /** ID of the element that describes this input (e.g. an error message).
   *  Forwarded as aria-describedby so screen readers announce the message on focus. */
  assistiveTextId?: string;
}

/**
 *  Input Pin Component
 *
 * @param id - Id for the input field.
 * @param name - Name for the input field.
 * @param disabled - If true, input is disabled.
 * @param required - If true, input is required.
 * @param size - Sets the size for the input field.
 * @param onFocus - Focus callback event.
 * @param onBlur - Blur callback event.
 * @param onChange - Change callback event.
 * @param error - If true, input gain error status.
 * @param type - Set the input type for the component.
 * @param inputRef - Set the reference for the input field node.
 * @param autoFocus - If true, input is focused by default.
 */
export const InputPin: React.FC<InputPinProps> = ({
  id,
  name,
  size = 'medium',
  disabled,
  onBlur,
  onFocus,
  onChange,
  onKeyDown,
  onClick,
  required,
  error,
  type = 'text',
  inputRef,
  autoFocus = false,
  defaultValue = '',
  autoComplete = 'off',
  assistiveTextId,
  ...rest
}) => {
  const [controlledValue, setControlledValue] = useState<InputPinAttributes['value']>(defaultValue);
  const [isFocused, setIsFocused] = useState<boolean>(autoFocus);

  const focusHandler = (e: FocusEvent<HTMLInputElement>) => {
    if (isFocused) return;
    setIsFocused(true);
    // Fixes cursor position on focus.
    requestAnimationFrame(() => {
      e.target.setSelectionRange(2, 2);
    });
    if (onFocus && !disabled) onFocus(e);
  };

  const blurHandler = (e: FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    if (onBlur && !disabled) onBlur(e);
  };

  const changeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const digitValue = value.charAt(value.length - 1);
    const isDigit = /^\d+$/.test(digitValue);
    const isEmpty = value === '';
    if (!isDigit && !isEmpty) return;

    setControlledValue(digitValue);

    if (onChange && !disabled) onChange(e);
  };

  const keydownHandler = (e: KeyboardEvent<HTMLInputElement>) => {
    // Prevents moving cursor if the target is already focused.
    if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) e.preventDefault();
    if (onKeyDown && !disabled) onKeyDown(e);
  };

  const mouseDownHandler = (e: MouseEvent<HTMLInputElement>) => {
    // Prevents moving cursor if the target is already focused.
    if (e.currentTarget === document.activeElement) e.preventDefault();
  };

  const mouseUpHandler = (e: MouseEvent<HTMLInputElement>) => {
    // Prevents moving cursor if the target is already focused.
    if (e.currentTarget === document.activeElement) e.preventDefault();
  };

  useEffect(() => {
    if (autoFocus && inputRef?.current) inputRef?.current.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run only once on mount
  }, []);

  useEffect(() => {
    setControlledValue(defaultValue);
  }, [defaultValue]);

  return (
    <div
      className={classNames('vds-input-pin', `vds-input-pin--${size}`, {
        'vds-input-pin--error': error,
        'vds-input-pin--focused': isFocused,
        'vds-input-pin--disabled': disabled,
      })}>
      <input
        id={id}
        name={name}
        type={type}
        required={required}
        pattern="\d*"
        disabled={disabled}
        onFocus={focusHandler}
        onBlur={blurHandler}
        onChange={changeHandler}
        onKeyDown={keydownHandler}
        onMouseDown={mouseDownHandler}
        onMouseUp={mouseUpHandler}
        onClick={!disabled && onClick ? onClick : undefined}
        ref={inputRef}
        value={controlledValue}
        inputMode="numeric"
        autoComplete={autoComplete}
        aria-invalid={error || undefined}
        aria-describedby={assistiveTextId}
        {...rest}
      />
    </div>
  );
};
